<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { RouterLink } from "vue-router"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import type { Services } from "@services"
import { formatIdSource } from "@core/helpers"

// ================================
// TYPES & INTERFACES
// ================================

export interface SourceAlreadyExistDonePayload {
    /** Identifiant Mongo de la source (pour rafraîchissement après fermeture du dialog) */
    _id_source: string
    idtype: string
    idsource: string
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        /** Identifiant unique de la source (compteur) */
        sourceId: string
        /** Identifiant réel du compteur (PDL ou PCE) */
        idsource: string
        /** Identifiant unique du bâtiment */
        buildingId: string
        /** true = parcours PCE, false = parcours PDL (pour les libellés) */
        isPce?: boolean
        /** Masquer le lien vers la page bâtiment après succès */
        hideBuildingRedirection?: boolean
        /** Ouverture depuis la page bâtiment (libellé bouton Quitter) */
        fromBuilding?: boolean
        /** true si la source est déjà rattachée au bâtiment courant (pas de bouton Partager) */
        sourceAlreadyInBuilding?: boolean
    }>(),
    {
        isPce: false,
        hideBuildingRedirection: false,
        fromBuilding: false,
        sourceAlreadyInBuilding: false,
    }
)

const emit = defineEmits<{
    done: [payload?: SourceAlreadyExistDonePayload]
    "click:quit": [fromBuilding: boolean]
}>()

// ================================
// SERVICES
// ================================

const services = inject<Services>("services")

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title_alert: "Ce compteur est déjà associé à votre compte",
            alert_message_pdl:
                "Le compteur PDL {idsource} est déjà associé à votre compte.",
            alert_message_pdl_same:
                "Le compteur PDL {idsource} est déjà associé à ce bâtiment.",
            alert_message_pce:
                "Le compteur PCE {idsource} est déjà associé à votre compte.",
            alert_message_pce_same:
                "Le compteur PCE {idsource} est déjà associé à ce bâtiment.",
            demande:
                "Souhaitez-vous l'ajouter à ce bâtiment pour pouvoir consulter ses données ?",
            success: "Le compteur a été ajouté au bâtiment avec succès.",
            success_go_building: "Vous pouvez le consulter depuis la page",
            building: "bâtiment",
            button_return: "Retour",
            button_share: "Ajouter au bâtiment",
        },
    },
})

// ================================
// FETCH WRAPPER (addBuildingSource)
// ================================

const addBuildingSourceFn = async () => {
    if (!services?.$addBuildingSource) {
        throw new Error("Service $addBuildingSource non disponible")
    }
    await services.$addBuildingSource({
        sourceId: props.sourceId,
        buildingId: props.buildingId,
        start_date: new Date().toISOString().slice(0, 10),
    })
}

const {
    fetch: fetchAddBuildingSource,
    isFetching,
    isSuccess,
    error: fetchError,
} = useFetchWrapper(addBuildingSourceFn)

// État local pour message affiché (succès ou erreur)
const message = ref("")
const isError = ref(false)

watch(
    [isSuccess, fetchError],
    ([success, err]) => {
        if (success) {
            message.value = t("success")
            isError.value = false
        } else if (err) {
            const detail = (
                err as {
                    response?: { data?: { detail?: { message?: string } } }
                }
            )?.response?.data?.detail
            message.value =
                detail?.message ?? err.message ?? "Une erreur est survenue"
            isError.value = true
        }
    },
    { immediate: true }
)

/** Terminé avec succès (affiche message et lien bâtiment) */
const isDone = computed(() => isSuccess.value)

/** Émettre done avec payload quand l’ajout au bâtiment réussit */
watch(isSuccess, (success) => {
    if (success) {
        emit("done", {
            _id_source: props.sourceId,
            idtype: props.isPce ? "pce" : "pdl",
            idsource: props.idsource,
        })
    }
})

/** Message d'alerte selon type (PDL/PCE) et même bâtiment ou non */
const getAlertMessage = computed(() => {
    const type = props.isPce ? "pce" : "pdl"
    const same = props.sourceAlreadyInBuilding
    return t(`alert_message_${type}${same ? "_same" : ""}`, {
        idsource: formatIdSource(props.idsource, props.isPce ? "pce" : "pdl"),
    })
})

// ================================
// ACTIONS
// ================================

function onQuit(): void {
    emit("click:quit", props.fromBuilding)
}

function onSubmit(): void {
    message.value = ""
    isError.value = false
    fetchAddBuildingSource().catch(() => {
        // Erreur gérée par useFetchWrapper / watch
    })
}
</script>

<template>
    <div class="source-already-exist flex flex-col gap-4">
        <BaseMessage
            severity="info"
            :pt="{
                content: 'flex flex-col justify-center',
                text: 'flex flex-col gap-6',
            }"
        >
            <!-- Titre avec icône -->
            <div v-if="!isDone" class="flex items-center justify-center gap-2">
                <i
                    class="pi pi-exclamation-circle text-xl"
                    aria-hidden="true"
                />
                <h3 class="text-lg font-semibold">
                    {{ t("title_alert") }}
                </h3>
            </div>

            <!-- Message alerte -->
            <p v-if="!isDone" class="text-center text-base">
                {{ getAlertMessage }}
            </p>

            <!-- Demande d'ajout au bâtiment -->
            <p
                v-if="!isDone && !sourceAlreadyInBuilding"
                class="text-center text-gray-600"
            >
                {{ t("demande") }}
            </p>

            <!-- Footer : message succès/erreur + boutons -->
            <div class="flex flex-col gap-4">
                <BaseMessage
                    v-if="message"
                    :severity="isError ? 'error' : 'success'"
                    class="w-full"
                >
                    <template v-if="!isError && !hideBuildingRedirection">
                        <span>{{ message }}</span>
                        {{ " " }}
                        <RouterLink
                            :to="`/building/${buildingId}/compteur`"
                            class="font-medium underline"
                        >
                            {{ t("success_go_building") }}
                            {{ " " }}
                            {{ t("building") }}
                        </RouterLink>
                    </template>
                    <template v-else>
                        {{ message }}
                    </template>
                </BaseMessage>
            </div>
        </BaseMessage>
        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                :label="t('button_return')"
                severity="secondary"
                @click="onQuit"
                :disabled="isFetching"
                class="min-w-[10rem] w-full md:w-auto"
            />
            <BaseButton
                v-if="!isDone && !sourceAlreadyInBuilding"
                :label="t('button_share')"
                :loading="isFetching"
                :disabled="isFetching"
                @click="onSubmit"
                class="min-w-[10rem] w-full md:w-fit"
            />
        </div>
    </div>
</template>
